import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, Typography
} from "@mui/material";
import {
  RESPONSE_TABS, RESPONSE_ERRORS, formatDateTime
} from "../../common/constants/scheduleModule/responseConstants.js";
import { STRINGS } from "../../common/constants/commonConstants.js";
import { fetchPtpResponseData, fetchPtpAging } from "../../services/scheduleModule/ptpResponseServices.js";
// Accounts comes from the SAME endpoint the Scorecard, Trends and PTP pages
// use, so "Accounts" means one thing everywhere: distinct Accountid in
// customerconvevent over the window.
import { getDashboardAccountsHourly as getAccounts }
  from "../../services/pbi/pbiservices.js";
import DatePickerComponent from "../../common/components/DatePickerComponent.jsx";

// ── THE NAMING, WRITTEN DOWN ───────────────────────────────────────
//
// Two rates look almost identical and are not. Both have the same numerator —
// the response count — and different denominators:
//
//   Response %   Responses / Sent       did the message land
//   RPC %        Responses / Accounts   did we reach the account
//
// One message per send, but an account can take several sends in a window, so
// Accounts is the smaller denominator and RPC % is the higher number. That is
// why the SAME COUNT appears twice on this page under two labels: "Responses"
// in Reach & engagement, measured against sends, and "RPC" in PTP performance,
// measured against accounts. Deliverability and conversion are different
// questions and they need different bases.
//
//   PTP     Kept + Promises      everything committed to
//   PTP %   PTP / RPC            of those reached, how many committed
//   Kept %  Kept / PTP           of what was committed, how much arrived
//   SL %    SmartLinks / RPC     a smart link is its own event, not part of PTP
//
// ⚠ UNCOMMITTED IS NEITHER SUMMED NOR SHOWN. Customers given the payment
// method who never selected one commit to nothing, so the figure adds to
// nothing and appears nowhere. FutureProc — the SCHEDULED half — was
// mislabelled Uncommitted; it is Promises, and it does feed PTP.
//
// ── THE PALETTE IS THE REPORT'S ──
// rpt1's PDF is the artifact people forward, so it is the reference: RPC blue,
// PTP green, Kept a darker shade of that green, Accounts brown.
const shade = (hex, pct) => {
  const n = parseInt(String(hex).slice(1), 16);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + c * pct)));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map(f).map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};
const KEPT_SHADE = -0.42;

const C_SENT        = "#0C447C";  // navy
const C_ACCOUNTS    = "#8A5524";  // brown
const C_RPC         = "#378ADD";  // blue
const C_PTP         = "#1D9E75";  // green
const C_SL          = "#D4537E";  // pink
const C_KEPT        = shade(C_PTP, KEPT_SHADE);   // dark green

// Chrome and the non-metric accents. BLUE drives buttons, sort arrows and the
// channel toggle; it names no measure.
const BLUE   = "#378ADD";
const PINK   = "#D4537E";
const AMBER  = "#BA7517";
const GREEN  = "#1D9E75";
const PURPLE = "#7F77DD";

// DPD bucket sort order — matches the backend `dpdSort` CASE in /getptpresponse.
const DPD_ORDER = {
  "1-9":   1, "10-19": 2, "20-29": 3, "30-39": 4,
  "40-49": 5, "50-59": 6, "60-89": 7, "90+":   8,
  "Other": 9,
};

const WRAPPING_COLS = new Set(["Message", "CampaignName", "CampaignScheduleName"]);

// Backend keys retained as-is; only the user-facing labels reflect the
// new vocabulary: SamedayProc → Kept, FutureProc → Promises,
// Future → Unscheduled. The legacy lumped ProcCount/ProcAmount/ProcPct
// have been dropped from the /getptpresponse SELECT.
const PTP_COUNT_COLS = new Set([
  "PtpCount", "SamedayProcCount", "FutureProcCount",
  "FutureCount", "SLCount",
]);
const PTP_AMOUNT_COLS = new Set([
  "PtpAmount", "SamedayProcAmount", "FutureProcAmount",
  "FutureAmount", "SLAmount",
]);

// One hue per metric, for the detail table's numeric cells. Everything not
// listed falls through to the rate-scale colouring below.
const COL_COLOR = {
  MsgsSent:           C_SENT,
  RpcCount:           C_RPC,
  RpcPct:             C_RPC,
  PtpCount:           C_PTP,
  PtpAmount:          C_PTP,
  PtpPct:             C_PTP,
  SamedayProcCount:   C_KEPT,
  SamedayProcAmount:  C_KEPT,
  SamedayProcPct:     C_KEPT,
  FutureCount:        PURPLE,
  FutureAmount:       PURPLE,
  FuturePct:          PURPLE,
  SLCount:            C_SL,
  SLAmount:           C_SL,
  SLPct:              C_SL,
};

const OPTOUT_COL = "OptOutCount";

// ── Helpers ────────────────────────────────────────────────────────
function responseColor(val) {
  const n = Number(val);
  if (isNaN(n) || n === 0) return "text.disabled";
  if (n >= 75) return GREEN;
  if (n >= 40) return BLUE;
  if (n >= 15) return AMBER;
  return PINK;
}

function optoutColor(val) {
  const n = Number(val);
  if (isNaN(n) || n === 0) return "text.disabled";
  if (n >= 10) return PINK;
  if (n >= 5)  return AMBER;
  if (n >= 1)  return AMBER;
  return "text.secondary";
}

function fmtCurrency(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1_000)     return `$${(num / 1_000).toFixed(2)}K`;
  return `$${num.toFixed(2)}`;
}

// Serialised from the date parts — toISOString() converts to UTC first, which
// shifts the date by a day in negative-offset zones. The accounts route wants
// YYYY-MM-DD; formatDateTime keeps serving the calls it already served, so
// each route gets the argument shape it expects.
function toLocalYMD(d) {
  if (!(d instanceof Date) || isNaN(d)) return "";
  const yy = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mo}-${dy}`;
}

const csvEscape = (v) => {
  if (v == null) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Column semantics (backend keys unchanged):
//   SamedayProc* → "Kept"        (CF-AI3-PAY, sameday pay)
//   FutureProc*  → PROMISES      (CF-AI4-PTP, future-proc with scheduled date)
//   Future*      → "Unscheduled" (no scheduled date)
//
// FutureProc has no columns any more. It feeds PTP and nothing else — see the
// header. It was labelled "Uncommitted", which it is not.
//
// Denominators, matching the tiles above and the funnel on the dashboards:
//   Response %     → vs Sent  (was labelled "RPC %"; the header now names the
//                              rate the column actually holds)
//   PTP %          → vs RPC   (derived client-side)
//   Kept %         → vs PTP   (derived client-side, overriding the route)
//   Unscheduled %  → vs RPC   (from /getptpresponse)
//   SL %           → vs RPC   (from /getptpresponse)
//   Opt-out %      → vs Sent  (deliverability, not a conversion)
//
// ⚠ THERE IS NO PER-ROW RPC %. It needs an account count per (DPD, message,
// campaign, schedule) and the accounts endpoint returns one number for the
// whole window. RPC % exists only on the tile.
const COLS = [
  { key: "DPD",                  label: "DPD",          width: 60,  align: "right" },
  { key: "Message",              label: "Message",      width: 220, align: "left"  },
  { key: "Char",                 label: "Char",         width: 55,  align: "right" },
  { key: "CampaignName",         label: "Campaign",     width: 130, align: "left"  },
  { key: "CampaignScheduleName", label: "Schedule",     width: 130, align: "left"  },
  { key: "MsgsSent",             label: "Sent",         width: 60,  align: "right" },
  { key: "RpcCount",             label: "RPC",          width: 55,  align: "right" },
  { key: "RpcPct",               label: "Response %",   width: 95,  align: "right", pct: true },
  { key: "PtpCount",             label: "PTP",            width: 80,  align: "right" },
  { key: "PtpAmount",            label: "PTP $",          width: 95,  align: "right", currency: true },
  { key: "PtpPct",               label: "PTP %",          width: 80,  align: "right", pct: true },
  { key: "SamedayProcCount",     label: "Same Day",       width: 85,  align: "right" },
  { key: "SamedayProcAmount",    label: "Same Day $",     width: 100, align: "right", currency: true },
  { key: "SamedayProcPct",       label: "Same Day %",     width: 95,  align: "right", pct: true },
  { key: "FutureCount",          label: "Unscheduled",    width: 120, align: "right" },
  { key: "FutureAmount",         label: "Unscheduled $",  width: 135, align: "right", currency: true },
  { key: "FuturePct",            label: "Unscheduled %",  width: 135, align: "right", pct: true },
  { key: "SLCount",              label: "SmartLink",    width: 80,  align: "right" },
  { key: "SLAmount",             label: "SL $",         width: 80,  align: "right", currency: true },
  { key: "SLPct",                label: "SL %",         width: 60,  align: "right", pct: true },
  { key: "OptOutCount",          label: "Opt-out",      width: 70,  align: "right" },
  { key: "OptOutPct",            label: "Opt-out %",    width: 80,  align: "right", pct: true },
];

const COL_GAP     = 12;
const GRID_COLS   = COLS.map(c => `${c.width}px`).join(" ");
const TABLE_WIDTH = COLS.reduce((s, c) => s + c.width, 0) + 32 + (COLS.length - 1) * COL_GAP;

function formatCellValue(col, raw) {
  if (col.currency) return fmtCurrency(raw);
  if (raw == null || raw === "") return "—";
  if (col.pct) {
    const n = Number(raw);
    if (isNaN(n)) return "—";
    return `${n.toFixed(1)}%`;
  }
  return raw;
}

function csvCellValue(col, raw) {
  if (raw == null || raw === "") return "";
  if (col.pct || col.currency) {
    const n = Number(raw);
    if (isNaN(n)) return "";
    return col.pct ? n.toFixed(1) : n.toFixed(2);
  }
  return raw;
}

function cellColor(col, raw) {
  if (col.key === "DPD")          return AMBER;
  if (col.key === "Char") {
    const n = Number(raw) || 0;
    if (n === 0)   return "text.disabled";
    if (n > 160)   return PINK;
    return "text.secondary";
  }
  if (WRAPPING_COLS.has(col.key)) return "text.primary";

  // One hue per metric, dimmed to disabled at zero so an empty cell reads as
  // empty rather than as a coloured zero.
  const metric = COL_COLOR[col.key];
  if (metric) {
    const n = Number(raw) || 0;
    return n > 0 ? metric : "text.disabled";
  }

  if (col.key === OPTOUT_COL) {
    const n = Number(raw) || 0;
    return n > 0 ? PINK : "text.disabled";
  }
  if (col.key === "OptOutPct") return optoutColor(raw);
  return responseColor(raw);
}

// ── Sub-components ─────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function KpiTile({ label, count, amount, percent, color }) {
  const hasPercent = percent !== null && percent !== undefined;
  const hasCount   = count   !== null && count   !== undefined;
  const hasAmount  = amount  !== null && amount  !== undefined;

  const heroValue = hasPercent ? percent : hasCount ? count : "—";
  const heroColor = (hasPercent || hasCount) ? color : "text.disabled";

  const showFooterCount = hasPercent && hasCount;

  return (
    <Box sx={{
      background: "#f7f7f7", borderRadius: 2,
      p: "10px 12px",
      flex: 1, minWidth: 0,
      display: "flex", flexDirection: "column", gap: "6px",
    }}>
      <Typography sx={{
        fontSize: 10, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </Typography>

      <Box sx={{ minHeight: 26 }}>
        <Typography sx={{
          fontSize: 20, fontWeight: 500, color: heroColor, lineHeight: 1.2,
        }}>
          {heroValue}
        </Typography>
      </Box>

      <Box sx={{ minHeight: 22 }}>
        {hasAmount ? (
          <Typography sx={{
            fontSize: 15, fontWeight: 500, color: "text.primary", lineHeight: 1.2,
          }}>
            {amount}
          </Typography>
        ) : (
          <Typography sx={{
            fontSize: 15, color: "text.disabled", lineHeight: 1.2,
          }}>
            —
          </Typography>
        )}
      </Box>

      <Box sx={{ minHeight: 18 }}>
        {showFooterCount ? (
          <Typography sx={{
            fontSize: 12, fontWeight: 500, color, lineHeight: 1.2,
          }}>
            {count}
          </Typography>
        ) : (
          <Typography sx={{
            fontSize: 12, color: "text.disabled", lineHeight: 1.2,
          }}>
            —
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── PTP aging row ──────────────────────────────────────────────────
// One row of KPI tiles for aging buckets (1d, 2d, ..., 7d, 7d+).
//
// ── IT HAS TO COVER BOTH EVENT CODES ──
// PTP is Kept + Promises, so "time to pay" is only the whole story if the
// aging buckets count CF-AI3-PAY (Kept / sameday pay) as well as CF-AI4-PTP
// (Promises / future-proc). Sameday events land almost entirely in the
// 1-day bucket; future-proc spreads across 2d–7d+.
//
// ⚠ WHETHER THEY DO IS A SERVER-SIDE FACT THIS COMPONENT CANNOT CHECK. The
// route hands back one `committed` metric and the client cannot see which
// event codes went into it. The cheap verification: the 1d bucket's count
// should be at or above the Kept tile's count for the same range. If it is
// materially below, /dashboard/ptp-aging is filtering to CF-AI4-PTP only and
// the fix is on the route, not here.
//
// The title states the claim explicitly so a wrong figure is a visible
// contradiction rather than a silent one.
//
// Aging = DATEDIFF(hour, create_dt, EventTime).
//
// Unscheduled PTP (staging_ptp_future_customer) is NOT rendered here — the
// upstream Cosmos docs don't capture a scheduled date, so the "scheduled −
// made" delta isn't computable.
//
// Buckets (exclusive): 1d (0-24h), 2d (24-48h), ..., 7d (144-168h), 7+d.
// Tile shape on this page: % hero / $ middle / count footer.
function PtpAgingRows({ startDate, endDate, channel, keptCount }) {
  const [aging,   setAging]   = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!startDate || !endDate) return;

    setLoading(true);

    fetchPtpAging({
      startDate: formatDateTime(startDate),
      endDate:   formatDateTime(endDate),
      channel:   String(channel || "texts").toLowerCase(),
    })
      .then(d => { if (!cancelled) setAging(d); })
      .catch(() => { if (!cancelled) setAging(null); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [startDate, endDate, channel]);

  if (loading || !aging) return null;

  const renderRow = (title, metric, accent) => {
    const buckets = metric?.buckets || [];
    const total   = metric?.total   || { count: 0, amount: 0 };
    if (total.count === 0) return null;

    // The 1-day bucket is where sameday payments land. If it comes back
    // smaller than the Kept count for the same window, the route is not
    // including CF-AI3-PAY and the row is understating time-to-pay.
    const oneDay  = Number(buckets[0]?.count) || 0;
    const missing = keptCount > 0 && oneDay + 1 < keptCount;

    return (
      <>
        <SectionTitle>{title}</SectionTitle>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: missing ? 0.75 : 1.5 }}>
          {buckets.map(b => (
            <KpiTile
              key={b.bucket}
              label={b.label}
              count={Number(b.count).toLocaleString()}
              amount={fmtCurrency(b.amount)}
              percent={`${Number(b.pct).toFixed(1)}%`}
              color={accent}
            />
          ))}
          <KpiTile
            label="Total"
            count={total.count.toLocaleString()}
            amount={fmtCurrency(total.amount)}
            percent={null}
            color={accent}
          />
        </Box>
        {missing && (
          <Typography sx={{ fontSize: 11, color: PINK, mb: 1.5, lineHeight: 1.5 }}>
            ⚠ The 1-day bucket holds {oneDay.toLocaleString()} events but Same Day is{" "}
            {keptCount.toLocaleString()} for the same range. Same-day payments land in
            the 1-day bucket, so this row is probably counting future-proc events only —
            check whether /dashboard/ptp-aging filters to CF-AI4-PTP.
          </Typography>
        )}
      </>
    );
  };

  return (
    <>
      {renderRow("PTP — time to pay (Same Day + Promises)", aging.committed, C_PTP)}
    </>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const PtpResponses = () => {
  const today = new Date();

  const [startDate,    setStartDate]    = useState(today);
  const [endDate,      setEndDate]      = useState(today);
  const [responseType, setResponseType] = useState(RESPONSE_TABS.TEXTS);
  const [data,         setData]         = useState([]);
  // Accounts worked in the window. Its own endpoint — the distinct scan is too
  // costly to ride along on the response read, which is why every page that
  // shows it fetches it separately.
  const [accounts,     setAccounts]     = useState(0);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [page,         setPage]         = useState(0);
  const [sortKey,      setSortKey]      = useState("PtpPct");
  const [sortDir,      setSortDir]      = useState("desc");
  const rowsPerPage = 25;

  const load = useCallback(async (start, end, type) => {
    setLoading(true);
    setError("");
    setPage(0);
    try {
      // Accounts is fetched alongside, and its failure is swallowed: a missing
      // account count costs one tile and one rate, where letting it throw would
      // cost the whole page.
      const [result, acc] = await Promise.all([
        fetchPtpResponseData({
          startDate:    formatDateTime(start),
          endDate:      formatDateTime(end),
          responseType: type,
        }),
        getAccounts({
          startDate:   toLocalYMD(start),
          endDate:     toLocalYMD(end),
          granularity: "day",
          channel:     String(type || "texts").toLowerCase(),
        }).catch(() => ({})),
      ]);
      setData(result || []);
      setAccounts(Number(acc?.account_count) || 0);
    } catch (e) {
      console.error(RESPONSE_ERRORS.FETCH_RESPONSE_ERROR, e);
      setError("Failed to load PTP response data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(today, today, RESPONSE_TABS.TEXTS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  // PTP is Kept + Promises, derived per row so the table, the sort,
  // and the CSV all read the same number as the PTP tile above.
  //
  // Denominators follow the funnel: PTP is measured against RPC, and Kept
  // against PTP. SamedayProcPct is overridden here rather than taken from
  // /getptpresponse, which still returns it against RpcCount — without the
  // override the "Kept %" column and the "Kept %" tile would disagree.
  const rows = useMemo(() => data.map(r => {
    const kept        = Number(r.SamedayProcCount)  || 0;
    const promises    = Number(r.FutureProcCount)   || 0;
    const keptAmt     = Number(r.SamedayProcAmount) || 0;
    const promAmt     = Number(r.FutureProcAmount)  || 0;
    const rpc         = Number(r.RpcCount)          || 0;
    const ptpCount    = kept + promises;
    return {
      ...r,
      PtpCount:        ptpCount,
      PtpAmount:       keptAmt + promAmt,
      PtpPct:          rpc      ? (ptpCount / rpc)      * 100 : 0,
      SamedayProcPct:  ptpCount ? (kept     / ptpCount) * 100 : 0,
    };
  }), [data]);

  const sortedData = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const dpdCmp = (DPD_ORDER[a.DPD] ?? 99) - (DPD_ORDER[b.DPD] ?? 99);
      if (dpdCmp !== 0) return dpdCmp;

      if (sortKey === "DPD") return sortDir === "asc" ? 0 : 0;

      const av = a[sortKey];
      const bv = b[sortKey];
      const an = Number(av);
      const bn = Number(bv);
      const cmp = !isNaN(an) && !isNaN(bn)
        ? an - bn
        : String(av ?? "").localeCompare(String(bv ?? ""));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);

  const pagedData  = useMemo(() =>
    sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  , [sortedData, page]);

  const totalPages = Math.ceil(data.length / rowsPerPage);

  const kpis = useMemo(() => {
    const totals = data.reduce((acc, r) => ({
      sent:         acc.sent         + (Number(r.MsgsSent)          || 0),
      rpc:          acc.rpc          + (Number(r.RpcCount)          || 0),
      samedayProc:  acc.samedayProc  + (Number(r.SamedayProcCount)  || 0),
      samedayProcAmt: acc.samedayProcAmt + (Number(r.SamedayProcAmount) || 0),
      futureProc:   acc.futureProc   + (Number(r.FutureProcCount)   || 0),
      futureProcAmt: acc.futureProcAmt + (Number(r.FutureProcAmount) || 0),
      future:       acc.future       + (Number(r.FutureCount)       || 0),
      futureAmt:    acc.futureAmt    + (Number(r.FutureAmount)      || 0),
      sl:           acc.sl           + (Number(r.SLCount)           || 0),
      slAmt:        acc.slAmt        + (Number(r.SLAmount)          || 0),
      optout:       acc.optout       + (Number(r.OptOutCount)       || 0),
    }), {
      sent: 0, rpc: 0,
      samedayProc: 0, samedayProcAmt: 0,
      futureProc: 0,  futureProcAmt:  0,
      future: 0,      futureAmt: 0,
      sl: 0,          slAmt: 0,
      optout: 0,
    });

    const pct    = n => totals.sent ? (n / totals.sent) * 100 : 0;
    const pctRpc = n => totals.rpc  ? (n / totals.rpc)  * 100 : 0;

    const ptpCount = totals.samedayProc    + totals.futureProc;
    const ptpAmt   = totals.samedayProcAmt + totals.futureProcAmt;

    return {
      ...totals,
      records:  data.length,
      accounts,
      ptpCount,
      ptpAmt,
      // Responses / Sent — deliverability. The "Responses" tile in Reach.
      responsePct: pct(totals.rpc),
      // Responses / Accounts — reach. The "RPC" tile in PTP performance.
      // Same numerator as above, smaller denominator, higher number.
      rpcPct:      accounts ? (totals.rpc / accounts) * 100 : 0,
      ptpPct:      pctRpc(ptpCount),
      keptPct:     ptpCount ? (totals.samedayProc    / ptpCount) * 100 : 0,
      keptAmtPct:  ptpAmt   ? (totals.samedayProcAmt / ptpAmt)   * 100 : 0,
      slPct:       pctRpc(totals.sl),
      optoutPct:   pct(totals.optout),
    };
  }, [data, accounts]);

  const exportDisabled = loading || sortedData.length === 0;

  const exportCsv = () => {
    if (!sortedData.length) return;

    const headers = COLS.map(c => csvEscape(c.label));
    const rows = sortedData.map(row =>
      COLS.map(col => csvEscape(csvCellValue(col, row[col.key]))).join(",")
    );
    const csv  = "\uFEFF" + [headers.join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);

    const now = new Date();
    const ts  = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}`;
    const channel = String(responseType).toLowerCase();

    const a = document.createElement("a");
    a.href     = url;
    a.download = `ptp_responses_${channel}_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const gridSx = {
    display: "grid",
    gridTemplateColumns: GRID_COLS,
    columnGap: `${COL_GAP}px`,
    width: `${TABLE_WIDTH}px`,
    px: 2,
  };

  // ── PTP PERFORMANCE: THE CONVERSION CHAIN, IN ORDER ──
  // Accounts → RPC → PTP → Kept, each rate against the step before it, then
  // SmartLinks which hangs off RPC rather than off PTP.
  //
  // Accounts carries no rate: it is the base the chain starts from, and a
  // percentage of nothing is not a number.
  //
  // The promise half (FutureProc) is still SUMMED — PTP is Kept + Promises and
  // the total needs both — but it has no tile and no column. Unscheduled keeps
  // its columns; it is a separate question from PTP.
  const fundedTiles = [
    { label: "Accounts",   count: accounts ? accounts.toLocaleString() : "—", amount: null,                             percent: null,                          color: C_ACCOUNTS },
    { label: "RPC",        count: kpis.rpc.toLocaleString(),          amount: null,                             percent: accounts ? `${kpis.rpcPct.toFixed(2)}%` : null, color: C_RPC },
    { label: "PTP",        count: kpis.ptpCount.toLocaleString(),     amount: fmtCurrency(kpis.ptpAmt),         percent: `${kpis.ptpPct.toFixed(2)}%`,  color: C_PTP  },
    { label: "Same Day",   count: kpis.samedayProc.toLocaleString(),  amount: fmtCurrency(kpis.samedayProcAmt), percent: `${kpis.keptPct.toFixed(2)}%`, color: C_KEPT },
    { label: "SmartLinks", count: kpis.sl.toLocaleString(),           amount: fmtCurrency(kpis.slAmt),          percent: `${kpis.slPct.toFixed(2)}%`,   color: C_SL   },
  ];

  // ── REACH & ENGAGEMENT: DELIVERABILITY ──
  // "Responses" rather than "RPC": the rate here is against SENT, which is the
  // Response % of the naming block at the top. The same count appears in the
  // PTP row above as "RPC", against accounts.
  const reachTiles = [
    { label: "DPD Msgs",  count: kpis.records.toLocaleString(), amount: null, percent: null,                                color: C_RPC  },
    { label: "Sent",      count: kpis.sent.toLocaleString(),    amount: null, percent: null,                                color: C_SENT },
    { label: "Responses", count: kpis.rpc.toLocaleString(),     amount: null, percent: `${kpis.responsePct.toFixed(2)}%`,   color: C_RPC  },
    { label: "Opt-outs",  count: kpis.optout.toLocaleString(),  amount: null, percent: `${kpis.optoutPct.toFixed(2)}%`,     color: PINK   },
  ];

  return (
    <Box sx={{ p: 0 }}>

      {/* ── Filters ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent
          label={STRINGS.START_DATE}
          date={startDate}
          onDateChange={setStartDate}
          marginRight={0}
        />
        <DatePickerComponent
          label={STRINGS.END_DATE}
          date={endDate}
          onDateChange={d => { if (d >= startDate) setEndDate(d); }}
          marginRight={0}
        />
        <Box
          onClick={() => load(startDate, endDate, responseType)}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            background: BLUE, color: "#fff", fontWeight: 500,
            alignSelf: "flex-end", mb: "1px",
            "&:hover": { background: "#2a6db5" },
          }}
        >
          Search
        </Box>

        <Box
          onClick={exportDisabled ? undefined : exportCsv}
          title={exportDisabled ? "No data to export" : "Export current view to CSV"}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            userSelect: "none",
            border: "0.5px solid",
            borderColor: exportDisabled ? "divider" : BLUE,
            background: "transparent",
            color: exportDisabled ? "text.disabled" : BLUE,
            fontWeight: 500,
            cursor: exportDisabled ? "default" : "pointer",
            opacity: exportDisabled ? 0.5 : 1,
            alignSelf: "flex-end", mb: "1px",
            display: "inline-flex", alignItems: "center", gap: "6px",
            transition: "all .15s",
            "&:hover": exportDisabled ? {} : { background: "rgba(55,138,221,0.06)" },
          }}
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>↓</span>
          Export CSV
        </Box>

        <Box sx={{
          display: "flex", ml: "auto",
          border: "0.5px solid", borderColor: "divider",
          borderRadius: "20px", overflow: "hidden",
          alignSelf: "flex-end",
        }}>
          {[RESPONSE_TABS.TEXTS, RESPONSE_TABS.EMAILS].map(ch => (
            <Box key={ch} onClick={() => {
              setResponseType(ch);
              load(startDate, endDate, ch);
            }} sx={{
              px: "14px", py: "5px", fontSize: 13,
              fontWeight:  responseType === ch ? 500 : 400,
              cursor: "pointer", userSelect: "none",
              background:  responseType === ch ? BLUE : "transparent",
              color:       responseType === ch ? "#fff" : "text.secondary",
              transition:  "all .15s",
            }}>
              {ch}
            </Box>
          ))}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          <SectionTitle>PTP performance</SectionTitle>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
            {fundedTiles.map(t => (
              <KpiTile key={t.label} {...t} />
            ))}
          </Box>

          <Typography sx={{
            fontSize: 11, color: "text.disabled", mb: 2, lineHeight: 1.5,
          }}>
            <b>RPC %</b> is responses over accounts; <b>Response %</b> in the row below is
            the same responses over texts sent. An account can take several sends in a
            window, so RPC % is the higher of the two. <b>PTP</b> is Same Day + Promises
            read against RPC, <b>Same Day %</b> is Same Day over PTP, and <b>SL %</b> is smart
            links over RPC — a smart link is its own event, not a part of PTP.
          </Typography>

          <SectionTitle>Reach &amp; engagement</SectionTitle>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            {reachTiles.map(t => (
              <KpiTile key={t.label} {...t} />
            ))}
          </Box>

          {/* ── AGING ROWS ────────────────────────────────────── */}
          {/* Same date range + channel as the filters above. keptCount is
              passed in so the row can check itself: sameday payments belong in
              the 1-day bucket, so a 1d count below Kept means the route is
              missing CF-AI3-PAY. */}
          <PtpAgingRows
            startDate={startDate}
            endDate={endDate}
            channel={responseType}
            keptCount={kpis.samedayProc}
          />

          <SectionTitle>Detail</SectionTitle>

          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflowX: "auto",
          }}>
            <Box sx={{
              ...gridSx,
              py: 1,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              fontSize: 11, fontWeight: 500,
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              {COLS.map(col => (
                <Box
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  sx={{
                    textAlign: col.align,
                    cursor: "pointer", userSelect: "none",
                    display: "flex", gap: 0.5,
                    alignItems: "center",
                    justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                    color: sortKey === col.key ? BLUE : "text.secondary",
                    "&:hover": { color: BLUE },
                    ...(WRAPPING_COLS.has(col.key) && { minWidth: 0 }),
                  }}
                >
                  {col.label}
                  {sortKey === col.key && (
                    <span style={{ fontSize: 10, flexShrink: 0 }}>
                      {sortDir === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </Box>
              ))}
            </Box>

            {pagedData.map((row, i) => (
              <Box key={row.Id ?? i} sx={{
                ...gridSx,
                py: "7px",
                fontSize: 12,
                borderBottom: "0.5px solid", borderColor: "divider",
                background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                "&:hover": { background: "rgba(55,138,221,0.06)" },
              }}>
                {COLS.map(col => {
                  const raw    = row[col.key];
                  const numVal = Number(raw) || 0;
                  const isText = WRAPPING_COLS.has(col.key);
                  return (
                    <Box key={col.key} sx={{
                      textAlign: col.align,
                      color: cellColor(col, raw),
                      fontWeight:
                        col.key === "DPD" || col.key === "MsgsSent"
                          ? 500
                          : numVal > 0 ? 500 : 400,
                      ...(isText && {
                        whiteSpace: "normal",
                        wordBreak:  "break-word",
                        minWidth:   0,
                      }),
                    }}>
                      {formatCellValue(col, raw)}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>

          {totalPages > 1 && (
            <Box sx={{
              display: "flex", justifyContent: "space-between",
              alignItems: "center", mt: 1, fontSize: 12,
              color: "text.secondary", px: 1,
            }}>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Showing {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, data.length)} of {data.length}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Box
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  sx={{
                    px: "10px", py: "3px", borderRadius: "20px",
                    border: "0.5px solid", borderColor: "divider",
                    cursor: page === 0 ? "default" : "pointer",
                    opacity: page === 0 ? 0.4 : 1,
                    "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                  }}
                >
                  ‹
                </Box>
                <span>Page {page + 1} of {totalPages}</span>
                <Box
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  sx={{
                    px: "10px", py: "3px", borderRadius: "20px",
                    border: "0.5px solid", borderColor: "divider",
                    cursor: page >= totalPages - 1 ? "default" : "pointer",
                    opacity: page >= totalPages - 1 ? 0.4 : 1,
                    "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                  }}
                >
                  ›
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PtpResponses;